import React from "react";
import { useTranslation } from "react-i18next";
import { BiSearch, BiReset } from "react-icons/bi";

interface ProtocolFiltersProps {
  facultyNumber: string;
  setFacultyNumber: (value: string) => void;
  studentName: string;
  setStudentName: (value: string) => void;
  filter: string;
  setFilter: (value: string) => void;
  subjectFilter: string;
  setSubjectFilter: (value: string) => void;
  specialityFilter: string;
  setSpecialityFilter: (value: string) => void;
  sessionTypeFilter: string;
  setSessionTypeFilter: (value: string) => void;
  courseFilter: string;
  setCourseFilter: (value: string) => void;
  onSearch: () => void;
  onReset: () => void;
  getUniqueValues: (field: string) => string[];
  setCurrentPage: (num: number) => void;
}

const ProtocolFilters: React.FC<ProtocolFiltersProps> = ({
  facultyNumber,
  setFacultyNumber,
  studentName,
  setStudentName,
  filter,
  setFilter,
  subjectFilter,
  setSubjectFilter,
  specialityFilter,
  setSpecialityFilter,
  sessionTypeFilter,
  setSessionTypeFilter,
  courseFilter,
  setCourseFilter,
  onSearch,
  onReset,
  getUniqueValues,
  setCurrentPage
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex flex-wrap gap-4 items-end my-4">
        <h4 className="text-sm font-bold">
          {t("Search by Faculty Number or Student Name")}
        </h4>
      </div>
      <div className="flex flex-wrap gap-4 items-end">
        <label htmlFor="facultyNumberInput" className="sr-only">
          {t("Faculty Number")}
        </label>
        <input
          id="facultyNumberInput"
          type="text"
          placeholder={t("Faculty Number")}
          value={facultyNumber}
          onChange={(e) => setFacultyNumber(e.target.value)}
          className="p-2 border border-gray-300 rounded dark:bg-black dark:border-gray-600 dark:text-white"
          aria-label={t("Faculty Number")}
        />

        <label htmlFor="studentNameInput" className="sr-only">
          {t("Student Name")}
        </label>
        <input
          id="studentNameInput"
          type="text"
          placeholder={t("Student Name")}
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
          className="p-2 border border-gray-300 rounded dark:bg-black dark:border-gray-600 dark:text-white"
          aria-label={t("Student Name")}
        />

        <button
          onClick={onSearch}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 inline-flex items-center gap-2"
        >
          <BiSearch className="w-5 h-5" />
          {t("Search")}
        </button>

        <button
          onClick={onReset}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 inline-flex items-center gap-2"
        >
          <BiReset className="w-5 h-5" />
          {t("Reset")}
        </button>
      </div>

      <div className="flex flex-wrap gap-4 items-end my-4">
        <h4 className="text-sm font-bold">
          {t("Filter by Subject, Speciality, and Session Type")}
        </h4>
      </div>
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex flex-wrap gap-4">
          <label htmlFor="searchProtocolsInput" className="sr-only">
            {t("SearchProtocols")}
          </label>
          <input
            id="searchProtocolsInput"
            type="text"
            placeholder={t("SearchProtocols")}
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="p-2 border border-gray-300 rounded dark:bg-black dark:border-gray-600 dark:text-white"
            aria-label={t("SearchProtocols")}
          />

          <select
            value={subjectFilter}
            onChange={(e) => { setSubjectFilter(e.target.value); setCurrentPage(1); }}
            className="p-2 border border-gray-300 rounded dark:bg-black dark:border-gray-600 dark:text-white"
          >
            <option value="">{t("All Subjects")}</option>
            {getUniqueValues("subjectName").map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>

          <select
            value={specialityFilter}
            onChange={(e) => { setSpecialityFilter(e.target.value); setCurrentPage(1); }}
            className="p-2 border border-gray-300 rounded dark:bg-black dark:border-gray-600 dark:text-white"
          >
            <option value="">{t("All Specialities")}</option>
            {getUniqueValues("specialityName").map((speciality) => (
              <option key={speciality} value={speciality}>
                {speciality}
              </option>
            ))}
          </select>

          <select
            value={sessionTypeFilter}
            onChange={(e) => { setSessionTypeFilter(e.target.value); setCurrentPage(1); }}
            className="p-2 border border-gray-300 rounded dark:bg-black dark:border-gray-600 dark:text-white"
          >
            <option value="">{t("All Session Types")}</option>
            {getUniqueValues("sessionTypeName").map((sessionType) => (
              <option key={sessionType} value={sessionType}>
                {sessionType}
              </option>
            ))}
          </select>
          <select
            value={courseFilter}
            onChange={(e) => { setCourseFilter(e.target.value); setCurrentPage(1); }}
            className="p-2 border border-gray-300 rounded dark:bg-black dark:border-gray-600 dark:text-white"
          >
            <option value="">{t("Courses")}</option>
            {[1, 2, 3, 4, 5, 6].map((course) => (
              <option key={course} value={course}>
                {course}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
};

export default ProtocolFilters; 